import { create } from 'zustand'
import client, { Socket } from 'socket.io-client'
import { authClient } from './auth-client'
import { queryClient } from '../api/query-client'

type SocketStateType = {
    socket: Socket | null
    isConnected: boolean
    activeRooms: Set<string>
    connect: () => void
    disconnect: () => void
    joinRoom: (roomId: string) => void
    leaveRoom: (roomId: string) => void
    emit: (eventName: string, data: any, callback: any) => void
    on: (eventName: string, handler: any) => void
    off: (eventName: string, handler: any) => void
}

export const useSocketStore = create<SocketStateType>((set, get) => ({
    socket: null,
    isConnected: false,
    activeRooms: new Set(),

    connect: () => {
        const existingSocket = get().socket
        if (existingSocket?.connected) {
            console.log('Socket already connected')
            return existingSocket
        }

        console.log('Initializing socket')

        const signOutAndRedirect = async () => {
            await authClient.signOut()
            queryClient.clear()
            window.location.href = '/login'
        }

        const socketUrl = import.meta.env.VITE_SOCKET_URL

        const socket = client(socketUrl, {
            path: '/api/socket',
            transports: ['websocket'],
            reconnection: true,
            reconnectionAttempts: 5,
            withCredentials: true,
        })

        socket.on('connect', () => {
            set({ isConnected: true })
        })

        socket.on('disconnect', (reason) => {
            set({ isConnected: false })
        })

        socket.on('connect_error', async (error: any) => {
            if (error.message.includes('Unauthorized')) {
                signOutAndRedirect()
            }
        })

        socket.on('error', (error: any) => {
            console.error('Socket encountered error:', error)
        })

        socket.on('unauthorized', async () => {
            logout()
        })

        set({ socket })
        return socket
    },

    disconnect: () => {
        const { socket } = get()
        if (socket) {
            socket.disconnect()
            set({ socket: null, isConnected: false })
        }
    },

    joinRoom: (roomId: string) => {
        const { socket, isConnected, activeRooms } = get()
        if (socket && isConnected && roomId && !activeRooms.has(roomId)) {
            socket.emit('subscribe', roomId)
            const newRooms = new Set(activeRooms)
            newRooms.add(roomId)
            set({ activeRooms: newRooms })
        }
    },

    leaveRoom: (roomId: string) => {
        const { socket, activeRooms } = get()
        if (socket && roomId && activeRooms.has(roomId)) {
            socket.emit('unsubscribe', roomId)
            const newRooms = new Set(activeRooms)
            newRooms.delete(roomId)
            set({ activeRooms: newRooms })
        }
    },

    emit: (eventName, data, callback) => {
        const { socket, isConnected } = get()
        if (socket && isConnected) {
            socket.emit(eventName, data, callback)
        } else {
            console.warn('Cannot emit - socket not connected')
        }
    },

    on: (eventName, handler) => {
        const { socket } = get()
        if (socket) {
            socket.on(eventName, handler)
        }
    },

    off: (eventName, handler) => {
        const { socket } = get()
        if (socket) {
            socket.off(eventName, handler)
        }
    },
}))
